import { Component, OnDestroy, OnInit, Signal, inject } from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {EActionStatus} from '@app/shared/enums/action-status.enum';
import {Store} from '@ngrx/store';
import {Subject, takeUntil, tap} from 'rxjs';
import {categoryListAction, resetCategoryList} from '../../state/category-list.actions';
import {selectCategoryListState} from '../../state/category-list.selectors';

import {NgTemplateOutlet} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {EProductStatus} from '@app/category/enum/product.status.enum';
import {ICategoryListState} from '@app/category/models/category-list-state.model';
import {ArrayUtils} from '@app/core/utils/array.utils';
import {IProduct} from '@app/shared/models/product.model';
import {selectCustomerOrganizationId} from '@app/shared/state/header/header.selector';
import {TranslatePipe} from '@ngx-translate/core';
import {Bind} from 'primeng/bind';
import {Button} from 'primeng/button';
import {Divider} from 'primeng/divider';
import {Message} from 'primeng/message';
import {Ripple} from 'primeng/ripple';
import {Skeleton} from 'primeng/skeleton';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';

@Component({
  selector: 'app-category-list',
  templateUrl: './category-list.component.html',
  styleUrl: './category-list.component.scss',
  imports: [
    Bind,
    Tabs,
    TabList,
    Ripple,
    Tab,
    TabPanels,
    TabPanel,
    Button,
    Divider,
    Message,
    NgTemplateOutlet,
    Skeleton,
    TranslatePipe,
  ],
})
export class CategoryListComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly store = inject(Store);

  private readonly unsubscribe$ = new Subject<void>();
  categoryListState: Signal<ICategoryListState>;
  organizationId: Signal<number | null | undefined>;
  initialState!: ICategoryListState;
  categoryId = 0;
  eActionStatus = EActionStatus;
  getNumberArray = ArrayUtils.getNumberArray;

  constructor() {
    this.store.dispatch(resetCategoryList());
    this.initialState = {
      status: EActionStatus.Initial,
      successResponse: [],
      failureReason: '',
      errorMessage: '',
    };

    this.organizationId = toSignal(this.store.select(selectCustomerOrganizationId), {initialValue: 0});

    this.categoryListState = toSignal(this.store.select(selectCategoryListState), {initialValue: this.initialState});
  }

  ngOnInit(): void {
    this.store
      .select(selectCustomerOrganizationId)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(customerOrganizationId => {
          if (customerOrganizationId) {
            this.store.dispatch(
              categoryListAction({
                request: {
                  organizationId: customerOrganizationId ?? 0,
                  status: EProductStatus.Active,
                },
              })
            );
          }
        })
      )
      .subscribe();

    this.route.queryParams?.subscribe(params => {
      if (params['id'] && params['id'] > 0) {
        this.categoryId = parseInt(params['id']);
      }
    });
  }

  selectedValue() {
    const response = this.categoryListState().successResponse;
    let selectedCategoryIndex;
    const selectedCategory = response?.find(x => x.id === this.categoryId);
    if (selectedCategory) {
      selectedCategoryIndex = response?.indexOf(selectedCategory);
    }
    return selectedCategoryIndex ?? 0;
  }

  selectProduct(product: IProduct): void {
    this.router.navigate(['product'], {
      queryParams: {id: product.id, variantId: 0},
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
